<template>
    <div :class="divClass">
        <h4 v-if="showHeader" :class="`${divClass}__heading`">
            INCLUDES:
        </h4>
        <div :class="`${divClass}__features`">
            <div
                v-for="feature in keyFeatures"
                :key="feature.featureName"
                :class="{ [`${divClass}__feature`]: true, 'not-active': !includedFeatures[feature.name] }"
                :data-original-title="tooltip(feature)"
                data-html="true"
                data-placement="bottom"
                data-toggle="tooltip"
            >
                <div :class="`${divClass}__feature__icon`">
                    <img :src="feature.icon" :alt="feature.featureName">
                </div>
                <h5 :class="`${divClass}__feature__name`">
                    {{ feature.featureName }}
                </h5>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "CompatibilityFeatures",
    props: {
        activeBuild: {
            type: Object,
            required: true
        },
        divClass: {
            type: String,
            required: true
        },
        showHeader: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            keyFeatures: [
                {
                    icon: "/images/icons/step-5-icons/rgb-lighting.svg",
                    featureName: "RGB Lighting",
                    name: "rgbLighting",
                    tooltip: {
                        included: "Your build is equipped with RGB components.",
                        missing: "Your build is not currently equipped with RGB components. To add, please navigate to the RGB tab or change case selection to i version."
                    }
                },
                {
                    icon: "/images/icons/step-5-icons/wifi.svg",
                    featureName: "WiFi",
                    name: "wifi",
                    tooltip: {
                        included: "Your build is WiFi ready.",
                        missing: "Your build does not have built-in WiFi capability. To add wifi capability, either change your motherboard or add a TP Link to your cart."
                    }
                },
                {
                    icon: "/images/icons/step-5-icons/bluetooth.svg",
                    featureName: "Bluetooth",
                    name: "bluetooth",
                    tooltip: {
                        included: "Your build has Bluetooth capability for use with wireless Bluetooth products.",
                        missing: "Your build does not have Bluetooth capability for use with wireless Bluetooth products. To add Bluetooth capability, please select another motherboard."
                    }
                },
                {
                    icon: "/images/icons/step-5-icons/usb-c.svg",
                    featureName: "USB-C",
                    name: "usbC",
                    tooltip: {
                        included: "The USB-C port on the front I/O panel of the case is functional.",
                        missing: "There is either no USB-C port on the front I/O panel of the case OR it is not functional. Ensure you have selected the proper case and motherboard to enable the USB-C port on the front I/O panel of the case."
                    }
                },
                {
                    icon: "/images/icons/step-5-icons/vr-ready.svg",
                    featureName: "VR Ready",
                    name: "vrReady",
                    tooltip: {
                        included: "Your build meets the minimum recommended specs to play VR games on the Oculus and HTC Vive.",
                        missing: "Your build does not meet the minimum recommended specs to play VR games on the Oculus and HTC Vive."
                    }
                },
                {
                    icon: "/images/icons/step-5-icons/stream-ready.svg",
                    featureName: "Stream Ready",
                    name: "streamReady",
                    tooltip: {
                        included: "Your build will be capable of streaming most games in 720p with 60fps or better, provided your connection has at least 10mpbs upload speed, and the streamed game typically runs at or above 60fps. Slower internet upload speeds may have mixed results.",
                        missing: "Your build does not meet the minimum recommended specs to stream at 720p with 60fps or better."
                    }
                },
                {
                    icon: "/images/icons/step-5-icons/monitor.svg",
                    featureName: "Monitor",
                    name: "monitor",
                    tooltip: {
                        included: "You currently have a monitor in your cart.",
                        missing: "You do not currently have a monitor with your build. Navigate to the Monitor tab to add."
                    }
                },
                {
                    icon: "/images/icons/step-5-icons/headset.svg",
                    featureName: "Headset",
                    name: "headset",
                    tooltip: {
                        included: "You currently have a headset in your cart.",
                        missing: "You do not currently have a headset with your build. Navigate to the Peripherals tab to add."
                    }
                },
                {
                    icon: "/images/icons/step-5-icons/keyboard.svg",
                    featureName: "Keyboard",
                    name: "keyboard",
                    tooltip: {
                        included: "You currently have a keyboard in your cart.",
                        missing: "You do not currently have a keyboard with your build. Navigate to the Peripherals tab to add."
                    }
                },
                {
                    icon: "/images/icons/step-5-icons/mouse.svg",
                    featureName: "Mouse",
                    name: "mouse",
                    tooltip: {
                        included: "You currently have a mouse in your cart.",
                        missing: "You do not currently have a mouse with your build. Navigate to the Peripherals tab to add."
                    }
                }
            ]
        }
    },
    computed: {
        includedFeatures() {
            const modules = ['cpuCooler', 'ram', 'motherboard', 'case', 'caseFans', 'peripherals', 'extras', 'rgbs'];
            let compatibility = {
                bluetooth: false,
                fanControl: false,
                headset: this.activeBuild.peripherals.some(item => item.type == "headset"),
                keyboard: this.activeBuild.peripherals.some(item => item.type == "keyboard"),
                monitor: Boolean(this.activeBuild.monitors[0].id),
                mouse: this.activeBuild.peripherals.some(item => item.type == "mouse"),
                rgbLighting: false,
                streamReady: this.activeBuild.ram.streaming_ready == 1,
                usbC: this.activeBuild.motherboard.external_usb_type_c == 1,
                vrReady: this.activeBuild.gpu.vr_capable == 1 && this.activeBuild.cpu.vr_capable == 1,
                wifi: false
            };

            modules.forEach((productModule) => {
                if (!(productModule in this.activeBuild)) {
                    return;
                }
                const product = this.activeBuild[productModule];

                if (Array.isArray(product)) {
                    product.forEach((variant) => {
                        compatibility = this.evaluateCompatibility(compatibility, variant);
                    });
                } else {
                    compatibility = this.evaluateCompatibility(compatibility, product);
                }
            });

            return compatibility;
        }
    },
    mounted() {
        $('[data-toggle="tooltip"]').tooltip({
            trigger: 'hover',
            html: true,
            template: '<div class="tooltip help-compatible" role="tooltip"><div class="tooltip-arrow"></div><div class="tooltip-inner"></div></div>'
        });
    },
    methods: {
        evaluateCompatibility(compatibility, product) {
            return {
                ...compatibility,
                bluetooth: compatibility.bluetooth || product.bluetooth == 1,
                fanControl: compatibility.fanControl || product.fan_control == 1,
                rgbLighting: compatibility.rgbLighting || product.cam_lighting == 1,
                wifi: compatibility.wifi || product.wifi == 1,
            };
        },
        tooltip(feature) {
            const text = this.includedFeatures[feature.name] ? "included" : "missing";
            return `<div style='text-align: left; font-size: 15px;'><b>${feature.featureName}</b><br>${feature.tooltip[text]}</div>`;
        }
    }
}
</script>

<style lang="scss">
.help-compatible {
    div {
        padding: 7px !important;
    }
}
</style>
